import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNotifications, getUnreadCount, markNotificationsRead, subscribePush } from '../lib/api';
import { Bell } from '@phosphor-icons/react';
import { Badge } from './ui/badge';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

async function registerPushSubscription() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
  
  const vapidKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
  if (!vapidKey) return;

  try {
    const registration = await navigator.serviceWorker.register('/sw-push.js');
    await navigator.serviceWorker.ready;
    
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return;
    
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey)
    });
    
    await subscribePush(subscription.toJSON());
  } catch (err) {
    console.warn('Push subscription failed:', err);
  }
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushRegistered, setPushRegistered] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await getUnreadCount();
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await getNotifications({ limit: 20 });
      setNotifications(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!pushRegistered) {
      registerPushSubscription();
      setPushRegistered(true);
    }
  }, [pushRegistered]);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleMarkAllRead = async () => {
    try {
      await markNotificationsRead([]);
      setUnreadCount(0);
      setNotifications(n => n.map(item => ({ ...item, is_read: true })));
    } catch {}
  };

  const handleNotifClick = (notif) => {
    if (!notif.is_read) {
      markNotificationsRead([notif.id]);
      setUnreadCount(c => Math.max(0, c - 1));
      setNotifications(n => n.map(item => item.id === notif.id ? { ...item, is_read: true } : item));
    }
    if (notif.link) navigate(notif.link);
    setOpen(false);
  };

  const timeAgo = (dateStr) => {
    const now = new Date();
    const d = new Date(dateStr);
    const diffMs = now - d;
    const mins = Math.floor(diffMs / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'task': return 'bg-cyan-500';
      case 'follow_up': return 'bg-purple-500';
      case 'overdue': return 'bg-red-500';
      default: return 'bg-blue-500';
    }
  };

  return (
    <div className="relative" ref={dropdownRef} data-testid="notification-bell">
      <button
        onClick={() => setOpen(!open)}
        className="relative p-2 rounded-sm hover:bg-zinc-100 transition-colors"
        data-testid="notification-bell-button"
      >
        <Bell size={22} weight={unreadCount > 0 ? 'fill' : 'regular'} className={unreadCount > 0 ? 'text-[#002FA7]' : 'text-zinc-600'} />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center" data-testid="notification-badge">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[380px] bg-white border border-zinc-200 rounded-sm shadow-lg z-50 max-h-[480px] flex flex-col" data-testid="notification-dropdown">
          <div className="flex items-center justify-between p-3 border-b border-zinc-100">
            <h3 className="text-sm font-semibold">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-[#002FA7] hover:underline"
                data-testid="mark-all-read"
              >
                Mark all as read
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-sm text-zinc-400">
                No notifications yet
              </div>
            ) : (
              notifications.map(notif => (
                <div
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  className={`px-4 py-3 border-b border-zinc-50 cursor-pointer hover:bg-zinc-50 transition-colors flex gap-3 ${!notif.is_read ? 'bg-blue-50/40' : ''}`}
                  data-testid={`notification-item-${notif.id}`}
                >
                  <div className={`w-2 h-2 rounded-full mt-2 shrink-0 ${notif.is_read ? 'bg-transparent' : getTypeColor(notif.type)}`} />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.is_read ? 'font-medium' : 'text-zinc-700'}`}>{notif.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">{notif.message}</p>
                    <p className="text-[11px] text-zinc-400 mt-1">{timeAgo(notif.created_at)}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
